// WP-37 (L-79): which deposit the checkout takes, from GET /api/checkout/deposit-policy.
//
//   fixed   : one deposit of `orderDeposit` pence for the whole order (ORDER_DEPOSIT_PENCE is set),
//             never more than the order's estimated total;
//   percent : one deposit of `orderDepositPercent`% of the estimated order total (goods only),
//             rounded down to the nearest pound (ORDER_DEPOSIT_PERCENT is set);
//   perItem : the £20 order deposit (`baseDeposit`) plus each product's own deposit, as before.
//
// Fetched once and kept for the life of the page. Until it is known (`status` 'loading', or
// 'failed' when the call did not work), product and basket pages show the per-item wording, which
// is what the API does when the setting is unset, and the checkout shows no deposit figure.
import { reactive } from 'vue';
import api from '@/services/api';

export const depositPolicy = reactive({
    status: 'loading', // 'loading' | 'ready' | 'failed'
    mode: null, // 'fixed' | 'perItem' once ready
    orderDeposit: null, // pence, fixed mode only
    orderDepositPercent: null, // 1 to 100, percent mode only
    baseDeposit: 2000, // pence, per-item mode
});

let request = null;

function isPositiveWholeNumber(value) {
    return Number.isInteger(value) && value > 0;
}

/** Starts the request once per page; a failed one is tried again on the next call. */
export function loadDepositPolicy() {
    if (request) return request;
    depositPolicy.status = 'loading';
    request = api.get('/api/checkout/deposit-policy')
        .then(({ data }) => {
            if (data && data.mode === 'fixed' && isPositiveWholeNumber(data.orderDeposit)) {
                Object.assign(depositPolicy, { status: 'ready', mode: 'fixed', orderDeposit: data.orderDeposit });
            } else if (data && data.mode === 'percent' && isPositiveWholeNumber(data.orderDepositPercent) && data.orderDepositPercent <= 100) {
                Object.assign(depositPolicy, { status: 'ready', mode: 'percent', orderDeposit: null, orderDepositPercent: data.orderDepositPercent });
            } else if (data && data.mode === 'perItem') {
                const base = isPositiveWholeNumber(data.baseDeposit) ? data.baseDeposit : 2000;
                Object.assign(depositPolicy, { status: 'ready', mode: 'perItem', orderDeposit: null, baseDeposit: base });
            } else {
                throw new Error('Unexpected deposit policy');
            }
        })
        .catch(() => {
            depositPolicy.status = 'failed';
            request = null;
        });
    return request;
}

/** True only once the API has said the deposit is fixed. */
export function isFixedDeposit() {
    return depositPolicy.status === 'ready' && depositPolicy.mode === 'fixed';
}

/** True only once the API has said the deposit is a percentage of the estimated order total. */
export function isPercentDeposit() {
    return depositPolicy.status === 'ready' && depositPolicy.mode === 'percent';
}

/** One deposit for the whole order (fixed or percentage): products carry no deposit of their own. */
export function isWholeOrderDeposit() {
    return isFixedDeposit() || isPercentDeposit();
}

/**
 * The percentage deposit the server will charge for this estimated total (pence): the percentage
 * rounded DOWN to whole pounds, never £0 (£1, or the estimate itself when under £1); null when the
 * estimate is unknown or zero (the server then takes the per-item deposit). Mirrors
 * percentOrderDeposit in worker/src/lib/deposit.ts.
 */
export function percentDepositFor(estimatedTotal) {
    if (!(estimatedTotal > 0)) return null;
    const pounds = Math.floor((estimatedTotal * depositPolicy.orderDepositPercent) / 10000);
    return pounds >= 1 ? pounds * 100 : Math.min(100, estimatedTotal);
}

/**
 * The fixed deposit the server will charge for an order with this estimated total (pence): the
 * fixed amount, or the estimate when that is smaller and above zero. Mirrors fixedOrderDeposit in
 * worker/src/lib/deposit.ts.
 */
export function fixedDepositFor(estimatedTotal) {
    const fixed = depositPolicy.orderDeposit;
    return estimatedTotal > 0 ? Math.min(fixed, estimatedTotal) : fixed;
}

/** "£30" for whole pounds, "£30.50" otherwise: for sentences, not for the money columns. */
export function formatDepositAmount(pence) {
    const whole = pence % 100 === 0;
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
    }).format(pence / 100);
}
